import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state on app startup
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');
        
        if (storedToken && storedUser) {
          try {
            // Parse stored user data
            const userData = JSON.parse(storedUser);
            
            // Set the token in axios headers
            axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
            
            // Restore session from stored data
            setToken(storedToken);
            setUser(userData);
            setIsAuthenticated(true);
          } catch (error) {
            // Invalid stored data, clear it
            console.log('Invalid stored user data, clearing auth data');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            delete axios.defaults.headers.common['Authorization'];
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Set axios header when token changes
  useEffect(() => {
    if (token) {
      setIsAuthenticated(true);
      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const login = async (username, password) => {
    try {
      // Clear any existing authorization header before login
      delete axios.defaults.headers.common['Authorization'];
      
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        username,
        password
      });

      const { token: newToken, role, user: userData, message } = response.data;
      
      if (newToken) {
        setToken(newToken);
        localStorage.setItem('token', newToken);
        const userInfo = { 
          name: username, 
          role, 
          id: userData ? userData.id : null 
        };
        localStorage.setItem('user', JSON.stringify(userInfo));
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setUser(userInfo);
        setIsAuthenticated(true);
        return { success: true, message };
      } else {
        return { success: false, message };
      }
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setIsLoading(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  };

  const patientLogin = async (phone, dob) => {
    try {
      // Clear any existing authorization header before login
      delete axios.defaults.headers.common['Authorization'];
      
      const response = await axios.post(`${API_URL}/api/auth/patient-login`, {
        phone,
        dob
      });
      const { token: newToken, role, message } = response.data;
      if (newToken) {
        setToken(newToken);
        localStorage.setItem('token', newToken);
        const userInfo = { phone, role };
        localStorage.setItem('user', JSON.stringify(userInfo));
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setUser(userInfo);
        setIsAuthenticated(true);
        return { success: true, message };
      } else {
        return { success: false, message };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const value = {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout,
    patientLogin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 